import React, { useCallback, useEffect, useRef, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { SelectButton } from 'primereact/selectbutton';
import { Tag } from 'primereact/tag';
import { Toast } from 'primereact/toast';
import { Message } from 'primereact/message';
import * as XLSX from 'xlsx';
import alertaService from '../../services/Alerta/AlertaService';
import { TagTipo, TagEvento, Codigo } from './alertaVisual';
import { fechaLocalIso } from './lecturaAlerta';
import { PANTALLAS, ORIGENES, GPS, UbicacionApertura } from './HistorialAperturas';

/**
 * Auditoria de lectura de alertas.
 *
 * Responde "quien vio que, cuando, cuantas veces y desde donde". Solo para
 * ADMINISTRADOR y SUPERVISOR CONSOLA REPORTES: el backend lo vuelve a
 * controlar, el menu no es la unica barrera.
 *
 * Tres miradas sobre los mismos datos:
 *  - POR VIAJE (por defecto): la unidad que revisa el guardia. Un viaje tiene N
 *    alertas y cada usuario pudo abrir cualquiera, varias veces. Por usuario:
 *    aperturas, cuantas de las N vio, si uso "Revisé" y cuando.
 *  - POR USUARIO: totales de cada uno en el rango.
 *  - CADA APERTURA: el registro fila por fila, con IP y GPS.
 *
 * Rango de hasta 31 dias; arranca en hoy (fecha de Ecuador).
 */

const VISTAS = [
    { label: 'Por viaje', value: 'viaje' },
    { label: 'Por usuario', value: 'usuario' },
    { label: 'Cada apertura', value: 'detalle' }
];

const OPCIONES_PANTALLA = [{ label: 'Todas', value: '' }].concat(
    Object.entries(PANTALLAS).map(([value, label]) => ({ label, value })));

const fecha = (v) => {
    if (!v) return '';
    const d = new Date(v);
    return isNaN(d.getTime()) ? '' : d.toLocaleString('es-EC');
};

const PAUSA_MS = 450;

const AuditoriaLectura = () => {
    const toast = useRef(null);
    const [filtros, setFiltros] = useState({
        desde: fechaLocalIso(0), hasta: fechaLocalIso(0), usuario: '', pantalla: '', texto: ''
    });
    const [vista, setVista] = useState('viaje');
    const [resp, setResp] = useState({ datos: [], resumen: [], porViaje: [] });
    const [cargando, setCargando] = useState(false);
    const [aviso, setAviso] = useState(null);
    const turno = useRef(0);

    const buscar = useCallback(async (f) => {
        const mio = ++turno.current;
        setCargando(true);
        setAviso(null);
        try {
            const r = await alertaService.auditoria(f);
            if (mio !== turno.current) return;
            if (r?.ok === false) {
                setAviso(r.mensaje);
                setResp({ datos: [], resumen: [], porViaje: [] });
            } else {
                setResp({ datos: r.datos || [], resumen: r.resumen || [], porViaje: r.porViaje || [] });
                if (r.recortado) setAviso('Se muestran las 2.000 aperturas más recientes. Achicá el rango para ver el resto.');
            }
        } catch (e) {
            if (mio !== turno.current) return;
            toast.current?.show({ severity: 'error', summary: 'No se pudo consultar la auditoría', life: 4000 });
        } finally {
            if (mio === turno.current) setCargando(false);
        }
    }, []);

    // Se consulta al dejar de tipear, igual que la consulta de alertas.
    useEffect(() => {
        const t = setTimeout(() => buscar(filtros), PAUSA_MS);
        return () => clearTimeout(t);
    }, [buscar, filtros]);

    const cambiar = (id, v) => setFiltros((p) => ({ ...p, [id]: v }));

    const exportar = () => {
        const wb = XLSX.utils.book_new();
        const viajes = [['Viaje', 'Placa', 'Candado', 'Contenedor', 'Booking', 'Transportista',
            'Alertas del viaje', 'Usuario', 'Nombre', 'Abrió el viaje (veces)', 'Aperturas de alertas', 'Alertas vistas', 'Revisó el viaje',
            'Primera apertura', 'Última apertura']];
        resp.porViaje.forEach((v) => (v.usuarios || []).forEach((u) => viajes.push([
            v.viajeId, v.placa, v.candado, v.contenedor, v.booking, v.transportista, v.alertasViaje,
            u.usuario, u.nombre, u.viajeAbierto || 0, u.aperturas, u.alertasVistas, u.revisado ? fecha(u.revisado) : 'No',
            fecha(u.primera), fecha(u.ultima)])));
        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(viajes), 'Por viaje');

        const usuarios = [['Usuario', 'Nombre', 'Aperturas', 'Alertas distintas', 'Viajes abiertos', 'Revisiones de viaje', 'Última apertura']];
        resp.resumen.forEach((u) => usuarios.push([u.usuario, u.nombre, u.aperturas, u.alertas,
            u.viajesAbiertos || 0, u.revisionesViaje, fecha(u.ultima)]));
        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(usuarios), 'Por usuario');

        const detalle = [['Fecha', 'Usuario', 'Nombre', 'Pantalla', 'Desde', 'IP', 'Latitud', 'Longitud',
            'Precisión (m)', 'GPS', 'Alerta', 'Tipo', 'Evento', 'Viaje', 'Placa', 'Candado', 'Contenedor', 'Booking']];
        resp.datos.forEach((a) => detalle.push([fecha(a.fecha), a.usuario, a.nombre, PANTALLAS[a.pantalla] || a.pantalla,
            ORIGENES[a.origen] || a.origen, a.ip, a.latitud, a.longitud, a.precision,
            a.ubicacionEstado === 'OK' ? 'OK' : (GPS[a.ubicacionEstado] || a.ubicacionEstado),
            a.monitoreoDetId, a.tipoEvento, a.evento, a.viajeId, a.placa, a.candado, a.contenedor, a.booking]));
        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(detalle), 'Cada apertura');

        XLSX.writeFile(wb, `auditoria_alertas_${filtros.desde}_a_${filtros.hasta}.xlsx`);
    };

    const tarjetaViaje = (v) => {
        const n = v.alertasViaje;
        return (
            <div key={v.viajeId ?? `a${v.ultima}`} className="col-12 xl:col-6">
                <div className="p-3 border-1 border-round surface-card h-full" style={{ borderColor: 'var(--surface-border)' }}>
                    <div className="flex flex-wrap gap-3 mb-2">
                        {[['Placa', v.placa], ['Candado', v.candado], ['Contenedor', v.contenedor], ['Booking', v.booking]]
                            .filter(([, x]) => x).map(([k, x]) => (
                                <div key={k}>
                                    <div className="text-xs text-color-secondary uppercase">{k}</div>
                                    <div className="font-medium"><Codigo valor={x} /></div>
                                </div>
                            ))}
                        <div className="ml-auto text-right text-sm text-color-secondary">
                            {v.transportista && <div className="font-semibold text-color">{v.transportista}</div>}
                            {n != null ? `${n} alertas en el viaje` : ''} · {v.aperturas} aperturas
                        </div>
                    </div>
                    <table className="w-full text-sm" style={{ borderCollapse: 'collapse' }}>
                        <thead>
                            <tr className="text-left text-color-secondary">
                                <th className="py-1 pr-2">Usuario</th>
                                <th className="py-1 pr-2">Abrió el viaje</th>
                                <th className="py-1 pr-2">Aperturas de alertas</th>
                                <th className="py-1 pr-2">Vio</th>
                                <th className="py-1 pr-2">Revisó el viaje</th>
                                <th className="py-1">Primera · última</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(v.usuarios || []).map((u) => (
                                <tr key={u.usuario} style={{ borderTop: '1px solid var(--surface-border)' }}>
                                    <td className="py-1 pr-2">
                                        <div className="font-medium">{u.usuario}</div>
                                        {u.nombre && u.nombre !== u.usuario &&
                                            <div className="text-xs text-color-secondary">{u.nombre}</div>}
                                    </td>
                                    <td className="py-1 pr-2">
                                        {u.viajeAbierto ? `${u.viajeAbierto} ${u.viajeAbierto === 1 ? 'vez' : 'veces'}` : '—'}
                                    </td>
                                    <td className="py-1 pr-2">{u.aperturas}</td>
                                    <td className="py-1 pr-2">
                                        {n ? `${u.alertasVistas} de ${n}` : u.alertasVistas}
                                    </td>
                                    <td className="py-1 pr-2">
                                        {u.revisado
                                            ? <Tag severity="success" icon="pi pi-check" value={fecha(u.revisado)} />
                                            : <Tag severity="warning" value="No" />}
                                    </td>
                                    <td className="py-1 text-xs text-color-secondary">
                                        {fecha(u.primera)}<br />{fecha(u.ultima)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    return (
        <div className="grid">
            <Toast ref={toast} />
            <div className="col-12">
                <div className="card">
                    <div className="flex flex-wrap justify-content-between align-items-center gap-3">
                        <span className="text-xl font-bold">Auditoría de lectura de alertas</span>
                        <div className="flex gap-2">
                            <Button label="Excel" icon="pi pi-file-excel" className="p-button-success p-button-sm"
                                    disabled={!resp.datos.length} onClick={exportar} />
                            <Button icon="pi pi-refresh" className="p-button-text" tooltip="Actualizar"
                                    onClick={() => buscar(filtros)} />
                        </div>
                    </div>

                    <div className="grid mt-2">
                        <div className="col-6 md:col-2">
                            <label htmlFor="aDesde" className="block text-xs text-color-secondary mb-1">Desde</label>
                            <input id="aDesde" type="date" className="p-inputtext w-full" value={filtros.desde}
                                   onChange={(e) => cambiar('desde', e.target.value)} />
                        </div>
                        <div className="col-6 md:col-2">
                            <label htmlFor="aHasta" className="block text-xs text-color-secondary mb-1">Hasta</label>
                            <input id="aHasta" type="date" className="p-inputtext w-full" value={filtros.hasta}
                                   onChange={(e) => cambiar('hasta', e.target.value)} />
                        </div>
                        <div className="col-12 md:col-3">
                            <label htmlFor="aUsuario" className="block text-xs text-color-secondary mb-1">Usuario</label>
                            <InputText id="aUsuario" className="w-full" value={filtros.usuario} autoComplete="off"
                                       placeholder="ej. dole.r10" onChange={(e) => cambiar('usuario', e.target.value)} />
                        </div>
                        <div className="col-12 md:col-2">
                            <label htmlFor="aPantalla" className="block text-xs text-color-secondary mb-1">Pantalla</label>
                            <Dropdown inputId="aPantalla" className="w-full" value={filtros.pantalla}
                                      options={OPCIONES_PANTALLA} onChange={(e) => cambiar('pantalla', e.value)} />
                        </div>
                        <div className="col-12 md:col-3">
                            <label htmlFor="aTexto" className="block text-xs text-color-secondary mb-1">
                                Contenedor, booking, candado o placa
                            </label>
                            <InputText id="aTexto" className="w-full" value={filtros.texto} autoComplete="off"
                                       placeholder="ej. 597" onChange={(e) => cambiar('texto', e.target.value)} />
                        </div>
                    </div>

                    <div className="flex flex-wrap align-items-center gap-3 mt-1">
                        <SelectButton value={vista} options={VISTAS} onChange={(e) => e.value && setVista(e.value)} />
                        <span className="text-sm text-color-secondary">
                            {cargando ? 'Buscando…'
                                : `${resp.porViaje.length} viajes · ${resp.resumen.length} usuarios · ${resp.datos.length} aperturas`}
                        </span>
                    </div>

                    {aviso && <div className="mt-3"><Message severity="warn" text={aviso} /></div>}

                    {vista === 'viaje' && (
                        <div className="grid mt-2">
                            {!cargando && resp.porViaje.length === 0 && !aviso &&
                                <div className="col-12 text-color-secondary">Nadie abrió alertas en este rango.</div>}
                            {resp.porViaje.map(tarjetaViaje)}
                        </div>
                    )}

                    {vista === 'usuario' && (
                        <DataTable value={resp.resumen} className="mt-2" responsiveLayout="scroll"
                                   emptyMessage="Nadie abrió alertas en este rango." dataKey="usuario">
                            <Column field="usuario" header="Usuario" sortable />
                            <Column field="nombre" header="Nombre" sortable />
                            <Column field="aperturas" header="Aperturas" sortable />
                            <Column field="alertas" header="Alertas distintas" sortable />
                            <Column field="viajesAbiertos" header="Viajes abiertos" sortable />
                            <Column field="revisionesViaje" header="Revisiones de viaje" sortable />
                            <Column field="ultima" header="Última apertura" sortable body={(u) => fecha(u.ultima)} />
                        </DataTable>
                    )}

                    {vista === 'detalle' && (
                        <DataTable value={resp.datos} className="mt-2" responsiveLayout="scroll" paginator rows={50}
                                   emptyMessage="Nadie abrió alertas en este rango." dataKey="id">
                            <Column field="fecha" header="Fecha" sortable body={(a) => fecha(a.fecha)}
                                    style={{ minWidth: '11rem' }} />
                            <Column field="usuario" header="Usuario" sortable />
                            <Column field="pantalla" header="Pantalla" sortable body={(a) => PANTALLAS[a.pantalla] || a.pantalla} />
                            <Column field="origen" header="Desde" sortable body={(a) => ORIGENES[a.origen] || a.origen} />
                            <Column field="ip" header="IP" body={(a) => <span style={{ fontFamily: 'monospace' }}>{a.ip || '—'}</span>} />
                            <Column header="Ubicación" body={(a) => <UbicacionApertura a={a} />} style={{ minWidth: '12rem' }} />
                            <Column field="tipoEvento" header="Tipo" body={(a) => <TagTipo tipo={a.tipoEvento} />} />
                            <Column field="evento" header="Evento" body={(a) => <TagEvento evento={a.evento} />}
                                    style={{ minWidth: '13rem' }} />
                            <Column field="placa" header="Placa" body={(a) => <Codigo valor={a.placa} />} />
                            <Column field="contenedor" header="Contenedor" body={(a) => <Codigo valor={a.contenedor} />} />
                            <Column field="booking" header="Booking" body={(a) => <Codigo valor={a.booking} />} />
                        </DataTable>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AuditoriaLectura;
